import {Injectable, signal} from '@angular/core';
import {RealtimeService} from './realtime.service';
import {HttpClient} from '@angular/common/http';
import {lastValueFrom, startWith} from 'rxjs';
import {LA, LACountsResponse, LAFilterParams, LAFiltersResponse, LaInformation, LAPaginatedResponse, PaginationInfo} from '../../models/la';
import {environment} from '../../environments/environment';
import {CS} from '../../models/cs';

@Injectable({
  providedIn: 'root'
})
export class LaService {

  laList = signal<LA[]>([])
  laPagination = signal<PaginationInfo | null>(null)
  laFilters = signal<LAFiltersResponse | null>(null)
  laCounts = signal<LACountsResponse | null>(null)
  laThemeList = signal<LaInformation[]>([])
  laTypeList = signal<LaInformation[]>([])

  private currentParams: LAFilterParams = { limit: 10, offset: 0 };

  constructor(
    private realtime: RealtimeService,
    private http: HttpClient
  ) {
    this.realtime.la.pipe(startWith(null))
      .subscribe(()=>{
        this.refreshCurrentPage();
      })
  }

  async refreshCurrentPage() {
    await this.getLAPaginated(this.currentParams);
  }

  async getLAPaginated(params: LAFilterParams = {}): Promise<LAPaginatedResponse> {
    this.currentParams = {
      limit: params.limit ?? this.currentParams.limit,
      offset: params.offset ?? this.currentParams.offset,
      search: params.search || undefined,
      users: params.users?.length ? params.users : undefined,
      date: params.date || undefined,
      today: params.today || undefined,
      sortBy: params.sortBy || this.currentParams.sortBy,
      sortOrder: params.sortOrder || this.currentParams.sortOrder,
    };
    const queryParams = new URLSearchParams();
    if (this.currentParams.limit !== undefined) queryParams.set('limit', this.currentParams.limit.toString());
    if (this.currentParams.offset !== undefined) queryParams.set('offset', this.currentParams.offset.toString());
    if (this.currentParams.search) queryParams.set('search', this.currentParams.search);
    if (this.currentParams.users && this.currentParams.users.length > 0) queryParams.set('users', this.currentParams.users.join(','));
    if (this.currentParams.date) queryParams.set('date', this.currentParams.date);
    if (this.currentParams.today) queryParams.set('today', 'true');
    if (this.currentParams.sortBy) queryParams.set('sortBy', this.currentParams.sortBy);
    if (this.currentParams.sortOrder) queryParams.set('sortOrder', this.currentParams.sortOrder);
    const queryString = queryParams.toString();
    const url = `${environment.backendUrl}/v2/lesgislative_actions${queryString ? `?${queryString}` : ''}`;
    const result = await lastValueFrom(this.http.get<LAPaginatedResponse>(url));
    this.laList.set(result.data);
    this.laPagination.set(result.pagination);
    this.laFilters.set(result.filters);
    this.laCounts.set(result.counts);
    return result;
  }

  async getLAById(laId: string): Promise<LA> {
    return await lastValueFrom(this.http.get<LA>(`${environment.backendUrl}/lesgislative_actions/${laId}`));
  }

  async searchLA(query: string = '', limit: number = 20): Promise<LA[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', limit.toString());
    return await lastValueFrom(this.http.get<LA[]>(
      `${environment.backendUrl}/v2/lesgislative_actions/search?${params.toString()}`
    ));
  }

  async getAllLaInformation(organizationId?: string){
    const result = await lastValueFrom(this.http.get<LaInformation[]>(`${environment.backendUrl}/la_information`));
    this.laThemeList.set(result.filter(la => la.type === 'theme'));
    this.laTypeList.set(result.filter(la => la.type === 'type'));
  }

  async upsertLa(id: string,cs: Partial<Omit<LA, 'id'>>) {
    const response = await lastValueFrom(this.http.put<LA>(`${environment.backendUrl}/lesgislative_actions/${id}`, cs));
    await this.refreshCurrentPage();
    return response;
  }

  async updateStatus(laId: string, status: string) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/lesgislative_actions/` + laId + '/status', {status}));
    await this.refreshCurrentPage();
  }

  async delete(laId: string) {
    await lastValueFrom(this.http.delete<{}>(`${environment.backendUrl}/lesgislative_actions/` + laId));
    await this.refreshCurrentPage();
  }

}
